import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { EventConfig, CustomScript } from '../interfaces/event-script.models';
import { ScriptValidatorService } from '../services/script-validator.service';

/**
 * Modo Expert com editor de código TypeScript
 * TODO: Integrar Monaco Editor quando disponível
 */
@Component({
  selector: 'app-expert-mode',
  templateUrl: './expert-mode.component.html',
  styleUrls: ['./expert-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ExpertModeComponent implements OnInit {
  @Input() eventConfig: EventConfig | null = null;
  @Output() configChanged = new EventEmitter<EventConfig>();

  code: string = '';
  validationErrors: string[] = [];
  validationWarnings: string[] = [];
  isValidating = false;

  // Exemplos de código
  readonly codeExamples = [
    {
      name: 'Desabilitar Tab',
      code: `// Desabilitar uma tab específica
api.disableTab('tab-configuracoes');`
    },
    {
      name: 'Condicional Simples',
      code: `// Executar ação baseado em condição
if (context.currentValue > 100) {
  api.disableTab('tab-avancado');
} else {
  api.enableTab('tab-avancado');
}`
    },
    {
      name: 'Múltiplas Ações',
      code: `// Executar múltiplas ações
if (context.currentValue === 'opcao1') {
  api.showTab('tab-1');
  api.hideTab('tab-2');
  api.enableComponent('campo-especial');
} else {
  api.hideTab('tab-1');
  api.showTab('tab-2');
  api.disableComponent('campo-especial');
}`
    },
    {
      name: 'Acessar Form Data',
      code: `// Acessar dados de outros campos
const formData = api.getFormData();
if (formData['campo-outro'] === 'valor-especial') {
  api.setValue('campo-destino', 'novo-valor');
}`
    }
  ];

  constructor(
    private validator: ScriptValidatorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.eventConfig && this.eventConfig.customScript) {
      this.code = this.eventConfig.customScript.code;
    } else {
      this.code = this.getDefaultCode();
    }
  }

  /**
   * Código padrão inicial
   */
  private getDefaultCode(): string {
    return `// Escreva seu código TypeScript aqui
// API disponível: api.showTab(), api.hideTab(), api.enableTab(), etc.
// Contexto: context.currentValue, context.previousValue, context.formData

`;
  }

  /**
   * Quando o código muda
   */
  onCodeChange(): void {
    this.validateCode();
  }

  /**
   * Valida o código
   */
  validateCode(): void {
    this.isValidating = true;
    this.validationErrors = [];
    this.validationWarnings = [];

    const result = this.validator.validate(this.code);
    
    if (!result.valid) {
      this.validationErrors = result.errors;
    }

    if (result.warnings) {
      this.validationWarnings = result.warnings;
    }

    // Emitir configuração atualizada
    const customScript: CustomScript = {
      code: this.code,
      validated: result.valid,
      errors: result.errors
    };

    const updatedConfig: EventConfig = {
      ...(this.eventConfig || {
        id: this.generateId(),
        eventType: 'onChange',
        enabled: true
      }),
      mode: 'expert',
      customScript
    };

    this.configChanged.emit(updatedConfig);

    this.isValidating = false;
    this.cdr.markForCheck();
  }

  /**
   * Aplica um exemplo de código
   */
  applyExample(example: any): void {
    this.code = example.code;
    this.validateCode();
    this.cdr.markForCheck();
  }

  /**
   * Limpa o código
   */
  clearCode(): void {
    this.code = this.getDefaultCode();
    this.validateCode();
    this.cdr.markForCheck();
  }

  /**
   * Gera ID único
   */
  private generateId(): string {
    return `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Verifica se pode salvar
   */
  canSave(): boolean {
    return this.code.trim().length > 0 && this.validationErrors.length === 0;
  }
}



